import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, Backdrop } from '@mui/material';
import { getReplenishmentThresholdsCsv } from 'api/replenishment/get-replenishment-thresholds-csv';
import { Download as DownloadIcon } from '@mui/icons-material';
import { getProductsCsv } from 'api/catalog/get-products-csv';
import FileUploadArea from 'components/FileUploadArea';
import { useFileState } from 'hooks/use-file-state';
import { useSnackbar } from 'hooks/use-snackbar';
import React, { useState, useEffect } from 'react';

interface ReplenishmentThresholdCsvImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => void;
  warehouseId: string;
  warehouseName?: string;
}

const ReplenishmentThresholdCsvImportDialog = ({
  open,
  onClose,
  onImport,
  warehouseId,
  warehouseName,
}: ReplenishmentThresholdCsvImportDialogProps) => {
  const { file, clearFile, handleFileChange } = useFileState();
  const [isLoading, setIsLoading] = useState(false);
  const snackbar = useSnackbar();

  // Clean state when dialog is dismantled (closed)
  useEffect(() => {
    if (!open) {
      clearFile();
      setIsLoading(false);
    }
  }, [open, clearFile]);

  const handleImport = () => {
    if (!file) return;
    setIsLoading(true);
    onImport(file);
    setIsLoading(false);
  };

  const handleExport = () => {
    setIsLoading(true);
    getProductsCsv({
      success: () => {
        snackbar.show('CSV exportado com sucesso', null, 'success');
        setIsLoading(false);
      },
      error: (err) => {
        snackbar.show('Falha ao exportar CSV', null, 'error');
        setIsLoading(false);
      },
    });
  };

  const handleExportThresholds = () => {
    setIsLoading(true);
    getReplenishmentThresholdsCsv(warehouseId, undefined, undefined, {
      success: () => {
        snackbar.show('CSV de níveis de reposição exportado com sucesso', null, 'success');
        setIsLoading(false);
      },
      error: (err) => {
        snackbar.show('Falha ao exportar CSV de níveis de reposição', null, 'error');
        setIsLoading(false);
      },
    });
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <Backdrop
        open={isLoading}
        sx={{
          position: 'absolute',
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle>Importação de Niveis de Reposição</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          {warehouseName && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'normal' }}>
              Armazém: {warehouseName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Formato CSV: <strong>location,sku,min,max</strong>
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={isLoading}
              sx={{ textTransform: 'none', width: '50%' }}
            >
              Exportar Produtos
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportThresholds}
              disabled={isLoading}
              sx={{ textTransform: 'none', width: '50%' }}
            >
              Exportar Níveis
            </Button>
          </Box>
        </Box>
        <FileUploadArea
          file={file}
          onFileChange={handleFileChange}
          onClearFile={clearFile}
          accept=".csv"
          disabled={isLoading}
          title="Carregar ficheiro CSV"
          subtitle="Arraste o ficheiro ou clique para selecionar"
          buttonText="Escolher ficheiro"
          dragTitle="Solte o ficheiro aqui"
          dragSubtitle="Solte para carregar"
          onError={(message) => snackbar.show(message, null, 'error')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={isLoading} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button onClick={handleImport} disabled={!file || isLoading} variant="contained" sx={{ textTransform: 'none', minWidth: 100 }}>
          {isLoading ? 'A processar...' : 'Importar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReplenishmentThresholdCsvImportDialog;
